import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Swal from 'sweetalert2';
import update from 'immutability-helper';
import fromPairs from 'lodash.frompairs';
import { DragDropContext } from 'react-beautiful-dnd';
import { connect } from 'react-redux';

import LoadingComponent from '../../shared/LoadingComponent';
import TileView from '../../shared/TileView';
import TileForm from '../../shared/TileForm';

import { TileStatusNavComponent } from './components/TileStatusNavComponent';
import { TileFilterSubNavComponent } from './components/TileFilterSubNavComponent';
import { EditTilesComponent } from './components/EditTilesComponent';
import { AudiencesModal, TopicsModal } from '../../shared/SettingsModal';
import {
  ClientAdminTilesRouter,
  TileManager,
  constants,
  helpers,
} from './utils';
import {
  initializeDnDContext,
  addNewColumnToDnDContextSection,
} from './utils/initializeDnDContext';
import {
  DragAndDropToSameColumn,
  DragAndDropToDifferentColumns,
  BuildSortParams,
  ProcessDragEnd,
  UpdateTiles,
  DropTileToEmptyDigest,
} from './utils/handleDrag';
import { filterByMonth, findValidMonth } from './utils/filtersHelpers';
import { Fetcher, ObjectArraysExist } from '../../lib/helpers';
import {
  setBuckets,
  setActiveStatus,
  setIsUserSiteAdmin,
  setTiles,
  setDragAndDropContext,
  moveTiles,
  setFilters,
  updateBucketTiles,
  generateBuckets,
  fetchNewTile,
  fetchTileData,
  toggleTileLoading,
  setAudiences,
  setTopics,
  setTilesPerRow,
  updateDragAndDropContext,
} from '../../lib/redux/actions';
import { setNotification } from '../../lib/redux/notification/notificationSlice';
import { emptyBucketsDates } from '../../lib/redux/reducers/dndContext';
import calculateTileRowSize from '../../lib/utils/calculateTileRowSize';
import { RebuildTiles, NewPlanDate } from './utils/tilesHelpers';
import { Section, ClientAdminTilesContainerWrapper } from './StyledComponents';
import i18n from '../../app/i18n';

const suggestedTilesStatus = 'user_submitted';

class ClientAdminTiles extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeStatus: '',
      tileStatusNav: props.tileStatusNav,
      loading: true,
      scrollLoading: true,
      alert: props.alert,
      appLoaded: false,
      audiences: props.audiences,
      audienceLoading: false,
      ribbonTags: props.ribbonTags,
      ribbonTagsLoading: false,
      buckets: props.buckets,
      isUserSiteAdmin: props.user.siteAdmin,
      tilesPerRow: props.tilesPerRow,
      tiles: props.tiles,
      dndContext: props.dndContext,
      shouldRefetchTile: false,
    };
    this.addTileToDigest = this.addTileToDigest.bind(this);
    this.boardSettingsPopulated = this.boardSettingsPopulated.bind(this);
    this.changeTileStatus = this.changeTileStatus.bind(this);
    this.createNewTilesDigest = this.createNewTilesDigest.bind(this);
    this.filterTilesByStatus = this.filterTilesByStatus.bind(this);
    this.filteredTiles = this.filteredTiles.bind(this);
    this.filterValue = this.filterValue.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleMenuAction = this.handleMenuAction.bind(this);
    this.moveTileBackToPlan = this.moveTileBackToPlan.bind(this);
    this.moveTileBackToUnschedule = this.moveTileBackToUnschedule.bind(this);
    this.moveTiles = this.moveTiles.bind(this);
    this.moveTileToProof = this.moveTileToProof.bind(this);
    this.populateBoardSettings = this.populateBoardSettings.bind(this);
    this.sanitizeBoardSettingsResults = this.sanitizeBoardSettingsResults.bind(
      this,
    );
    this.selectStatus = this.selectStatus.bind(this);
    this.setTileStatuses = this.setTileStatuses.bind(this);
    this.syncSettingsState = this.syncSettingsState.bind(this);
    this.tileBuilderPatch = this.tileBuilderPatch.bind(this);
    this.tileDigestPlanDates = this.tileDigestPlanDates.bind(this);
    this.toggleMonthCollapse = this.toggleMonthCollapse.bind(this);
    this.triggerModal = this.triggerModal.bind(this);
    this.unmountModal = this.unmountModal.bind(this);
    this.setTilesPerRow = this.setTilesPerRow.bind(this);
    this.showOldFilters = this.showOldFilters.bind(this);
    this.moveTileToNewDigest = this.moveTileToNewDigest.bind(this);
    this.refetchTile = this.refetchTile.bind(this);
  }

  componentDidMount() {
    window.Airbo.TileAdminActionObserver.init();
    this.selectStatus();
    TileManager.fetchAllTiles(this.setTileStatuses, this.state.activeStatus);
    window.addEventListener('popstate', this.selectStatus);
    window.Airbo.PubSub.subscribe(
      'reactTileChangeHandler',
      this.tileBuilderPatch,
    );
    window.Airbo.PubSub.subscribe('/tile-admin/tile-deleted', (e, payload) => {
      this.tileBuilderPatch(e, {
        tileId: payload.tile.selector.split('=')[1].slice(0, -1),
      });
    });
    window.addEventListener('resize', this.setTilesPerRow);
  }

  componentWillUnmount() {
    window.Airbo.PubSub.unsubscribe('reactTileChangeHandler');
    window.Airbo.PubSub.unsubscribe('/tile-admin/tile-deleted');
    window.removeEventListener('popstate', this.selectStatus);
    window.removeEventListener('resize', this.setTilesPerRow);
  }

  setTilesPerRow() {
    this.setState({ tilesPerRow: this.props.tilesPerRow }, () => {
      this.initializeDragAndDropContext();
    });
  }

  initializeDragAndDropContext() {
    const emptyBuckets = emptyBucketsDates(this.props.buckets);

    const planDndContext = initializeDnDContext(
      this.filteredTiles(),
      'plan',
      this.props.tilesPerRow,
    );

    const activeDndContext = initializeDnDContext(
      this.filteredTiles(),
      'active',
      this.props.tilesPerRow,
    );

    const archiveDndContext = initializeDnDContext(
      this.filteredTiles(),
      'archive',
      this.props.tilesPerRow,
    );

    const draftDndContext = initializeDnDContext(
      this.filteredTiles(),
      'draft',
      this.props.tilesPerRow,
    );

    const newPlanDnDContext = emptyBuckets.reduce((acc, date) => {
      if (this.state.activeStatus === 'plan' && !acc[date]) {
        return addNewColumnToDnDContextSection(acc, date);
      }
      return acc;
    }, planDndContext);

    const newState = {
      ...this.state,
      dndContext: {
        plan: {
          ...newPlanDnDContext,
          tilesPerRow: this.props.tilesPerRow,
        },
        active: {
          ...activeDndContext,
          tilesPerRow: this.props.tilesPerRow,
        },
        archive: {
          ...archiveDndContext,
          tilesPerRow: this.props.tilesPerRow,
        },
        draft: {
          ...draftDndContext,
          tilesPerRow: this.props.tilesPerRow,
        },
      },
    };

    this.props.setDragAndDropContext(newState.dndContext);

    this.setState(newState);
  }

  boardSettingsPopulated() {
    const boardSettings = {
      audiences: this.props.audiences,
      ribbonTags: this.props.ribbonTags,
    };
    return ObjectArraysExist(boardSettings, ['audiences', 'ribbonTags']).some(
      e => e,
    );
  }

  sanitizeBoardSettingsResults(resp) {
    const audiences = this.props.ctrl.audiencesEnabled
      ? resp.audiences.map(camp => {
          return helpers.sanitizeAudienceResponse(camp.audience);
        })
      : null;
    const ribbonTags = resp.ribbonTags.reduce(
      (result, tag) =>
        result.concat([helpers.sanitizeAudienceResponse(tag.ribbon_tag)]),
      [],
    );
    return { audiences, ribbonTags };
  }

  syncSettingsState(newSettingsState) {
    const { audiences, ribbonTags } = newSettingsState;

    this.props.setAudiences(audiences);
    this.props.setTopics(ribbonTags);
  }

  unmountModal() {
    this.setState({ alert: null });
  }

  populateBoardSettings() {
    if (!this.boardSettingsPopulated()) {
      this.setState({ audienceLoading: true, ribbonTagsLoading: true });

      Fetcher.xmlHttpRequest({
        path: '/api/v1/board_settings',
        method: 'GET',
        success: resp => {
          const { audiences, ribbonTags } = this.sanitizeBoardSettingsResults(
            resp,
          );
          const newState = {
            audienceLoading: false,
            ribbonTagsLoading: false,
          };

          this.props.setAudiences(audiences);
          this.props.setTopics(ribbonTags);
          this.setState({ ...newState });
        },
      });
    }
  }

  setTileStatuses(rawTiles, statuses) {
    const tiles = rawTiles.ignored.count
      ? helpers.sanitizeTileData(rawTiles)
      : rawTiles;
    const {
      plan: { tiles: planTiles },
    } = tiles;

    const tileStatusNav = [...Object.keys(statuses)]
      .reverse()
      .reduce((result, status) => {
        const tileCount = tiles[status] ? tiles[status].count : 0;
        const page = tileCount <= 16 ? 0 : 1;
        const filter = {
          audience: null,
          month: null,
          ribbonTag: null,
          sortType: null,
          year: null,
        };
        const insertStatus = {};
        insertStatus[status] = {
          tileCount,
          page,
          filter,
          uiDisplay: statuses[status],
        };
        return Object.assign(insertStatus, result);
      }, {});

    this.props.setTiles(tiles);
    this.props.generateBuckets(planTiles);
    this.props.setFilters(tileStatusNav);

    this.setState(
      {
        tileStatusNav,
        loading: false,
        scrollLoading: false,
        appLoaded: true,
        tiles,
      },
      () => {
        this.initializeDragAndDropContext();
      },
    );
  }

  changeTileRowSize(activeStatus) {
    const rowSize = calculateTileRowSize(activeStatus);
    this.props.setTilesPerRow(rowSize);
  }

  selectStatus(status) {
    const statuses = Object.keys(constants.TILE_STATUS);
    const activeStatus =
      statuses.indexOf(status) > -1
        ? status
        : ClientAdminTilesRouter.getRoute();

    this.props.setActiveStatus(activeStatus);
    this.changeTileRowSize(activeStatus);

    this.setState({ activeStatus }, () => {
      if (this.state.tiles) {
        this.props.setIsUserSiteAdmin(this.state.isUserSiteAdmin);
        this.props.setTiles(this.props.tiles);
        this.props.updateDragAndDropContext(this.props.tiles);
      }
    });

    if (typeof status === 'string') {
      ClientAdminTilesRouter.to(activeStatus);
    }
    // The following should be removed as React overhaul replaces old jQuery code
    setTimeout(() => {
      if (activeStatus === 'user_submitted') {
        window.Airbo.SuggestionBox.init();
      }
      if (activeStatus === 'active' || activeStatus === 'archive') {
        window.Airbo.TileStatsModal.init();
      }
      window.Airbo.TileManager.init();
    }, 100);
    // End block that needs to be removed
  }

  triggerModal(args) {
    const alert = helpers.swalModal({
      ...args,
      onCancel: () => {
        this.setState({ alert: null });
      },
    });
    this.setState({ alert });
  }

  changeTileStatus(tile, forceStatus) {
    if (this.state.activeStatus === 'archive' && !forceStatus) {
      this.triggerModal({
        ...constants.POST_AGAIN_MODAL_TEXT,
        onConfirm: () => {
          this.changeTileStatus(tile, 'active');
          this.setState({ alert: null });
        },
      });
    } else {
      const tileManager = new TileManager(tile.id, this);
      tileManager.toggleLoading();
      const newStatus =
        forceStatus || constants.STATUS_CYCLE[this.state.activeStatus];
      const { count } = this.props.tiles.draft; // Extraneous code used to patch connection between jQuery and React -- Delete when Share is moved to Edit
      tileManager.changeTileStatus(newStatus, this.state.activeStatus, count);
    }
  }

  tileDigestPlanDates() {
    const existingTileDigestDates = Object.keys(
      TileManager.groupBy(this.props.tiles.plan.tiles, 'planDate'),
    );
    return fromPairs(
      existingTileDigestDates
        .filter(dateString => dateString !== 'null')
        .map(dateString => {
          const dateObject = moment(dateString).toDate();
          return [dateObject, dateObject];
        }),
    );
  }

  handleMenuAction(tile, action, event) {
    if (action === 'post' || action === 'delete') {
      const actionConfirm = {
        post: (actTile, component) => {
          component.changeTileStatus(actTile, 'active');
        },
        delete: (actTile, component) => {
          this.props.setNotification({
            variant: 'delete',
            content: i18n.t('edit:tileStatusNotifications.deleteTile', {
              headline: actTile.headline,
            }),
            title: i18n.t('edit:tileStatusNotifications.delete'),
            disappear: true,
          }),
          component.handleMenuAction(actTile, 'deleteConfirm');
        },
      };
      this.triggerModal({
        ...constants[`${action}_MENU_ALERT_TEXT`],
        onConfirm: () => {
          actionConfirm[action](tile, this);
          this.setState({ alert: null });
        },
      });
    } else if (action === 'unscheduled') {
      event.stopPropagation();
      if (tile.status === 'draft') {
        this.moveTileBackToUnschedule(tile.id);
      }
    } else {
      const tileManager = new TileManager(tile.id, this);
      tileManager.performMenuAction(action);

      if (action === 'copy') {
        this.props.toggleTileLoading({
          tileId: tile.id,
          activeStatus: this.props.activeStatus,
        });
      }
    }
  }

  handleFilterChange(value, action, target) {
    let filter = value;
    let { buckets } = this.props;

    if (filter && filter.value === 'audience-settings') {
      AudiencesModal();
      return;
    }

    if (filter && filter.value === 'ribbonTag-settings') {
      TopicsModal();
      return;
    }

    const changeFilter =
      !value ||
      !this.props.tileStatusNav[this.props.activeStatus].filter[target] ||
      this.props.tileStatusNav[this.props.activeStatus].filter[target].label !==
        value.label;

    if (changeFilter) {
      const validMonth = target === 'month' && filter !== null;

      if (validMonth && filter.value !== -1) {
        const humanizedMonth = findValidMonth(filter.value, buckets);
        filter = update(filter, { humanizedMonth: { $set: humanizedMonth } });

        buckets = filterByMonth(humanizedMonth, buckets);
      } else if (validMonth && filter.value === -1) {
        buckets = filterByMonth('All Months', buckets);
      } else if (filter === null) {
        const currentMonth = moment().get('month');
        const humanizedMonth = findValidMonth(currentMonth, buckets);
        buckets = filterByMonth(humanizedMonth, buckets);
      }

      const tileStatusNav = update(this.props.tileStatusNav, {
        [this.props.activeStatus]: { filter: { [target]: { $set: filter } } },
      });

      this.props.setBuckets(buckets);
      this.props.setFilters(tileStatusNav);

      this.setState(
        {
          tileStatusNav,
          buckets,
          loading: false,
          scrollLoading: false,
        },
        () => {
          this.initializeDragAndDropContext();
        },
      );
    }
  }

  refetchTile(value) {
    this.setState({ shouldRefetchTile: value });
  }

  // Hacky patch to hold jQuery and React together until builder is overhauled
  tileBuilderPatch(event, payload) {
    try {
      const tile = new TileManager(payload.tileId, this);
      if (event.type === '/tile-admin/tile-deleted') {
        tile.removeTileFromCollection();
      } else {
        tile.refresh();
        this.props.fetchTileData(payload.tileId);
        this.refetchTile(true);
      }
    } catch (e) {
      this.props.fetchNewTile(payload.tileId);
    }
  }

  toggleMonthCollapse(month, collapsed) {
    const previousBuckets = { ...this.props.buckets };
    const bucketToggle = this.props.buckets[month];

    const buckets = update(previousBuckets, {
      [month]: {
        collapsed: {
          $set: collapsed === undefined ? !bucketToggle.collapsed : collapsed,
        },
      },
    });

    this.props.setBuckets(buckets);
    this.setState({ buckets });
  }

  createNewTilesDigest(date) {
    const newDate = moment(date, 'DD-MM-YYYY');
    const monthBucket = newDate.format('YYYY-MM');
    const parsedDate = newDate.format('YYYY-MM-DD');
    const previousBuckets = { ...this.props.buckets };
    const previousBucket = previousBuckets[monthBucket];
    const { digests: existingDigests } = previousBucket;
    const existingDigest = existingDigests[parsedDate];

    if (existingDigest) {
      Swal.fire(
        'error',
        `Tile Digest on ${newDate.format('MMM Do')} already exists.`,
        'error',
      );
      return;
    }

    const newDigests = update(existingDigests, {
      $merge: {
        [parsedDate]: { tiles: [] },
      },
    });
    const newDigestCount = previousBucket.digestCount + 1;

    const updatedBuckets = update(previousBuckets, {
      [monthBucket]: {
        $merge: { digests: newDigests, digestCount: newDigestCount },
      },
    });
    let newDndContextSection = addNewColumnToDnDContextSection(
      this.props.dndContext[this.props.activeStatus],
      parsedDate,
    );

    this.props.setBuckets(updatedBuckets);

    const draggedToNewDigest = this.props.dndContext.draggedToNewDigest;

    let previousTileId, nextTileId;

    if (draggedToNewDigest) {
      const result = this.moveTileToNewDigest(
        draggedToNewDigest,
        newDndContextSection,
        parsedDate,
      );

      newDndContextSection = result.newDndContextSection;
      previousTileId = result.previousTileId;
      nextTileId = result.nextTileId;
    }

    const newPlanDndContext = {
      plan: {
        ...newDndContextSection,
      },
    };

    const newDndContext = {
      ...this.props.dndContext,
      ...newPlanDndContext,
    };

    this.props.setDragAndDropContext({
      ...newDndContext,
      draggedToNewDigest: null,
    });

    if (draggedToNewDigest) {
      const tiles = this.props.tiles;
      const stateTiles = tiles[this.props.activeStatus].tiles;
      const { tileId } = { ...draggedToNewDigest };
      const movedTile = stateTiles.find(tile => tile.id.toString() === tileId);
      const movedTileIndex = stateTiles.indexOf(movedTile);
      movedTile.planDate = parsedDate;
      movedTile.bucketMonth = newDate.format('MMMM YYYY');
      stateTiles[movedTileIndex] = movedTile;

      const newTiles = update(tiles, {
        [this.props.activeStatus]: {
          tiles: {
            $set: stateTiles,
          },
          count: {
            $set: stateTiles.length,
          },
        },
      });

      const sortParams = BuildSortParams(
        true,
        false,
        parsedDate,
        stateTiles,
        previousTileId,
        nextTileId,
        draggedToNewDigest.tileId,
      );

      this.props.setTiles(newTiles);
      this.props.updateDragAndDropContext(newTiles);
      TileManager.sortTiles(sortParams, this.props.showErrorNotification);
    }
  }

  moveTileToNewDigest(draggedToNewDigest, dndContextSection, parsedDate) {
    // This means that we dropped a tile in a empty month container and this tile will be the first one to the new digest
    const source = {
      index: draggedToNewDigest.index,
      droppableId: draggedToNewDigest.droppableId,
    };
    const { tileId } = { ...draggedToNewDigest };
    const destination = { droppableId: parsedDate, index: 0 };
    const newColumn = { id: parsedDate, title: parsedDate, tilesIds: [] };
    const originalColumn = dndContextSection.columns[draggedToNewDigest.droppableId];
    const {
      newDndContextSection,
      previousTileId,
      nextTileId,
    } = DragAndDropToDifferentColumns(
      dndContextSection,
      originalColumn,
      newColumn,
      source,
      destination,
      tileId,
      this.props.activeStatus,
    );

    UpdateTiles(newColumn, newDndContextSection, this.props.updateBucketTiles);
    UpdateTiles(originalColumn, newDndContextSection, this.props.updateBucketTiles);

    document.querySelector(`#single-tile-${tileId}`).style.display = 'block';

    return { newDndContextSection, previousTileId, nextTileId };
  }

  // eslint-disable-next-line class-methods-use-this
  addTileToDigest(date) {
    const url = '/client_admin/tiles/new';
    const tileForm = window.Airbo.TileFormModal;
    window.Airbo.TileManager.planDate = date !== null ? date : null;
    window.Airbo.TileManager.tileDigestPlanDates = this.tileDigestPlanDates();
    tileForm.init(window.Airbo.TileManager);
    tileForm.open(url);
  }

  moveTileToProof(tileId) {
    const previousTiles = { ...this.props.tiles };
    const idsToChange = [tileId];
    const previousTile = previousTiles.plan.tiles.find(
      item => tileId === item.id,
    );
    const newPlanTiles = previousTiles.plan.tiles.filter(
      item => tileId !== item.id,
    );

    // Backend API call that actually moves tile.
    TileManager.batchUpdateStatus(idsToChange, 'draft', response => {
      const newDraftTiles = response.concat(previousTiles.draft.tiles);

      const tiles = update(previousTiles, {
        plan: {
          count: { $set: newPlanTiles.length },
          tiles: { $set: newPlanTiles },
        },
        draft: {
          count: { $set: newDraftTiles.length },
          tiles: { $set: newDraftTiles },
        },
      });

      this.props.setTiles(tiles);

      this.setState({ tiles }, () => {
        this.initializeDragAndDropContext();
      });

      window.Airbo.PubSub.publish('updateShareTabNotification', {
        number: newDraftTiles.length,
      });
    });
  }

  moveTileBackToUnschedule(tileId) {
    const idsToChange = [tileId];

    const tiles = this.rebuildDraftAndPlanTiles(tileId);

    this.props.setTiles(tiles);

    const statusChange = () =>
      TileManager.batchUpdateStatus(idsToChange, 'plan', () => {
        this.setState({ tiles }, () => {
          this.initializeDragAndDropContext();
        });
        window.Airbo.PubSub.publish('updateShareTabNotification', {
          number: tiles.draft.count,
        });
      });

    TileManager.batchPlanDateUpdate(idsToChange, null, statusChange);
  }

  rebuildDraftAndPlanTiles(tileId) {
    const previousTiles = { ...this.props.tiles };
    const tileAttributes = {
      bucketMonth: null,
      planDate: null,
    };

    const previousTile = previousTiles.draft.tiles.find(
      item => tileId === item.id,
    );

    const newTile = Object.assign({}, previousTile, tileAttributes);

    const newPlanTiles = [newTile].concat(previousTiles.plan.tiles);
    const newDraftTiles = previousTiles.draft.tiles.filter(
      item => tileId !== item.id,
    );

    const tiles = update(previousTiles, {
      plan: {
        count: { $set: newPlanTiles.length },
        tiles: { $set: newPlanTiles },
      },
      draft: {
        count: { $set: newDraftTiles.length },
        tiles: { $set: newDraftTiles },
      },
    });

    return tiles;
  }

  moveTileBackToPlan(tileId) {
    const previousTiles = { ...this.props.tiles };
    const previousTile = previousTiles.draft.tiles.find(
      item => tileId === item.id,
    );

    this.props.moveTiles(
      [previousTile.id],
      constants.STATUSES['draft'],
      constants.STATUSES['plan']
    );
  }

  moveTiles(prevStatus, newStatus) {
    const previousTiles = { ...this.props.tiles };
    const idsToChange = previousTiles[prevStatus].tiles.map(item => item.id);

    this.props.moveTiles(
      idsToChange,
      prevStatus,
      newStatus
    );
  }

  filterValue(field) {
    return (
      this.props.tileStatusNav[this.props.activeStatus] &&
      this.props.tileStatusNav[this.props.activeStatus].filter[field] &&
      this.props.tileStatusNav[this.props.activeStatus].filter[field].value
    );
  }

  filterTilesByStatus() {
    if (this.props.activeStatus === 'plan') {
      const planTiles = this.props.tiles[this.props.activeStatus].tiles;
      const suggestedTiles = this.props.tiles[suggestedTilesStatus].tiles;

      return planTiles.concat(suggestedTiles);
    }
    return this.props.tiles[this.props.activeStatus].tiles;
  }

  filteredTiles() {
    let tiles = this.filterTilesByStatus();
    const validMonths = [0, 1, 2, 4, 5, 6, 7, 8, 9, 10, 11];

    const yearFilter = this.filterValue('year');

    if (yearFilter) {
      tiles = tiles.filter(
        tile =>
          moment(tile[`${this.state.activeStatus}Date`]).year() === yearFilter,
      );
    }

    const monthFilter = this.filterValue('month');

    if (validMonths.includes(monthFilter)) {
      tiles = tiles.filter(
        tile =>
          moment(tile[`${this.state.activeStatus}Date`]).month() ===
          monthFilter,
      );
    }

    const ribbonTagFilter = this.filterValue('ribbonTag');

    if (ribbonTagFilter) {
      tiles = tiles.filter(tile => tile.ribbonTagId === ribbonTagFilter);
    }

    const audienceFilter = this.filterValue('audience');

    if (audienceFilter) {
      tiles = tiles.filter(tile => tile.audienceId === audienceFilter);
    }

    return tiles.slice().sort((a, b) => b.position - a.position);
  }

  onDragEnd(result) {
    const { source, destination, draggableId } = result;

    if (!ProcessDragEnd(source, destination)) {
      return;
    }

    // droping tile to a empty month digest
    if (destination.droppableId.match('new-digest')) {
      DropTileToEmptyDigest(
        this.props.dndContext,
        source,
        destination,
        draggableId,
        this.props.setDragAndDropContext,
      );

      return;
    }

    const { dndContext, tiles } = this.props;
    const originalColumn =
      dndContext[this.state.activeStatus].columns[source.droppableId];
    const newColumn =
      dndContext[this.state.activeStatus].columns[destination.droppableId];
    const unscheduledPlanDate = '0000-00-00';
    const genericPlanDate = 'generic';
    const planTab = this.state.activeStatus === 'plan';
    const unscheduledTab = newColumn.title === unscheduledPlanDate;
    const planOrUnscheduledTab = planTab || unscheduledTab;
    const movingToUnscheduled = planTab && unscheduledTab;
    const movingToPlan = planTab && !unscheduledTab;
    const movingFromGenericColumn = originalColumn.title === genericPlanDate;
    const { activeStatus } = this.state;

    let dragAndDropHandler;
    let planDate;

    if (originalColumn === newColumn) {
      dragAndDropHandler = DragAndDropToSameColumn;
    } else {
      dragAndDropHandler = DragAndDropToDifferentColumns;
    }

    const {
      newDndContextSection,
      previousTileId,
      nextTileId,
    } = dragAndDropHandler(
      dndContext[activeStatus],
      originalColumn,
      newColumn,
      source,
      destination,
      draggableId,
    );

    if (movingToPlan) {
      const movedTile = dndContext[activeStatus].tiles[draggableId];

      window.Airbo.Utils.ping('Add Tile to Digest', {
        tile_name: movedTile.headline,
        tab: window.Airbo.Utils.currentTab(),
        digest_date: destination.droppableId,
      });

      UpdateTiles(newColumn, newDndContextSection, this.props.updateBucketTiles);
      UpdateTiles(originalColumn, newDndContextSection, this.props.updateBucketTiles);
    }

    planDate = NewPlanDate(
      movingToUnscheduled,
      movingToPlan,
      newDndContextSection,
      newColumn,
      draggableId,
    );
    const newTiles = RebuildTiles(
      this.props.activeStatus,
      tiles,
      planDate,
      draggableId,
      previousTileId,
      nextTileId,
    );

    const sortParams = BuildSortParams(
      planOrUnscheduledTab,
      movingFromGenericColumn,
      planDate,
      tiles[this.props.activeStatus].tiles,
      previousTileId,
      nextTileId,
      draggableId,
    );

    this.props.setTiles(newTiles);
    this.props.updateDragAndDropContext(newTiles);
    TileManager.sortTiles(sortParams, this.props.showErrorNotification);
  }

  tileStatusForNav() {
    const { user_submitted, ...tileStatusNav } = this.props.tileStatusNav;

    return tileStatusNav;
  }

  showOldFilters() {
    return (
      this.props.activeStatus !== 'user_submitted' &&
      this.props.activeStatus !== 'draft' &&
      this.props.activeStatus !== 'plan'
    );
  }

  render() {
    return (
      <ClientAdminTilesContainerWrapper>
        <TileStatusNavComponent
          activeStatus={this.props.activeStatus}
          tileStatusNav={this.props.tileStatusNav}
          createNewTilesDigest={this.createNewTilesDigest}
          selectStatus={this.selectStatus}
          statuses={this.tileStatusForNav()}
          tiles={this.props.tiles}
          appLoaded={this.state.appLoaded}
          handleFilterChange={this.handleFilterChange}
          audiences={this.props.audiences}
          ribbonTags={this.props.ribbonTags}
          populateBoardSettings={this.populateBoardSettings}
          audienceLoading={this.state.audienceLoading}
          ribbonTagsLoading={this.state.ribbonTagsLoading}
          audiencesEnabled={this.props.ctrl.audiencesEnabled}
          isUserSiteAdmin={this.state.isUserSiteAdmin}
          moveTiles={this.moveTiles}
        />
        {this.showOldFilters() && (
          <TileFilterSubNavComponent
            appLoaded={this.state.appLoaded}
            activeStatus={this.props.activeStatus}
            tileStatusNav={this.props.tileStatusNav}
            handleFilterChange={this.handleFilterChange}
            audiences={this.props.audiences}
            ribbonTags={this.props.ribbonTags}
            populateBoardSettings={this.populateBoardSettings}
            audienceLoading={this.state.audienceLoading}
            ribbonTagsLoading={this.state.ribbonTagsLoading}
            audiencesEnabled={this.props.ctrl.audiencesEnabled}
          />
        )}
        {this.state.loading ? (
          <LoadingComponent />
        ) : (
          <DragDropContext onDragEnd={this.onDragEnd.bind(this)}>
            <Section className="manage_tiles  js-ca-tiles-index-module-tab-content">
              <EditTilesComponent
                addTileToDigest={this.addTileToDigest}
                changeTileStatus={this.changeTileStatus}
                createNewTilesDigest={this.createNewTilesDigest}
                handleMenuAction={this.handleMenuAction}
                isUserSiteAdmin={this.isUserSiteAdmin}
                moveTileBackToPlan={this.moveTileBackToPlan}
                moveTileToProof={this.moveTileToProof}
                toggleMonthCollapse={this.toggleMonthCollapse}
                {...this.state}
                tiles={this.filteredTiles()}
                activeStatus={this.props.activeStatus}
                activeFilters={
                  this.props.tileStatusNav[this.props.activeStatus].filter
                }
                dndContext={this.props.dndContext}
              />
            </Section>
          </DragDropContext>
        )}
        {this.props.alert || this.state.alert}
        {this.state.scrollLoading && !this.state.loading && (
          <LoadingComponent />
        )}
        {this.props.activeStatus && (
          <TileView
            refetchTile={this.refetchTile}
            shouldRefetchTile={this.state.shouldRefetchTile}
          />
        )}
        <TileForm />
      </ClientAdminTilesContainerWrapper>
    );
  }
}

ClientAdminTiles.propTypes = {
  ctrl: PropTypes.shape({
    audiencesEnabled: PropTypes.bool,
  }),
  user: PropTypes.shape({
    siteAdmin: PropTypes.bool,
  }),
};

const mapStateToProps = state => {
  const dragAndDropContext = {
    ...state.dndContext,
    tilesPerRow: state.tilesPerRow,
  };

  return {
    tilesPerRow: state.tilesPerRow,
    updatingTile: state.updatingTile,
    tiles: state.tiles.tiles,
    buckets: state.tiles.buckets,
    activeStatus: state.status.activeStatus,
    isUserSiteAdmin: state.status.isUserSiteAdmin,
    dndContext: dragAndDropContext,
    alert: state.status.alert,
    tileStatusNav: state.status.tileStatusNav,
    audiences: state.status.audiences,
    ribbonTags: state.status.ribbonTags,
  };
};

const mapDispatchToProps = dispatch => ({
  setBuckets: buckets => dispatch(setBuckets(buckets)),
  setActiveStatus: status => dispatch(setActiveStatus(status)),
  setIsUserSiteAdmin: value => dispatch(setIsUserSiteAdmin(value)),
  setTiles: tiles => dispatch(setTiles(tiles)),
  setDragAndDropContext: newContext =>
    dispatch(setDragAndDropContext(newContext)),
  moveTiles: (tilesIds, prevStatus, newStatus) =>
    dispatch(moveTiles(tilesIds, prevStatus, newStatus)),
  setFilters: filters => dispatch(setFilters(filters)),
  updateBucketTiles: data => dispatch(updateBucketTiles(data)),
  generateBuckets: tiles => dispatch(generateBuckets(tiles)),
  fetchNewTile: tileId => dispatch(fetchNewTile(tileId)),
  fetchTileData: tileId => dispatch(fetchTileData(tileId)),
  toggleTileLoading: data => dispatch(toggleTileLoading(data)),
  setAudiences: data => dispatch(setAudiences(data)),
  setTopics: data => dispatch(setTopics(data)),
  setTilesPerRow: data => dispatch(setTilesPerRow(data)),
  updateDragAndDropContext: tiles => dispatch(updateDragAndDropContext(tiles)),
  setNotification: ({ variant, content, title, disappear }) =>
    dispatch(setNotification({ variant, content, title, disappear })),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ClientAdminTiles);
